/**
 * Archive Page
 *
 * A component to handle the archive page
 *
 */

/**
 * React dependencies
 */

import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import LoadMoreButton from '../LoadMoreButton';

/**
 * DWT hooks
 */

const PostsArchive = ({ queries }) => {
  const [ articleData, setArticleData ] = useState( [] );
  const [ articlePageCount, setArticlePageCount ] = useState( 0 );
  const [ articlePageCurrent, setArticlePageCurrent ] = useState( 1 );
  const [ archiveType, setArchiveType ] = useState( '' );
  const [ archiveTitle, setArchiveTitle ] = useState( '' );
  const [ loading, setLoading ] = useState( true );

  const setupRequest = async( endpoint, method, body = null ) => {
    const baseUrl = window.location.origin;
    const ajaxNonce = typeof ajax_object !== 'undefined' ? ajax_object.nonce : null;

    const response = await fetch( `${baseUrl}/${endpoint}`, {
      method,
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        // eslint-disable-next-line camelcase, no-undef
        'X-WP-Header': ajaxNonce
      },
      body
    });

    if ( ( endpoint.includes( 'wp' ) ) && ( response.headers.get( 'X-WP-Total' ) !== null ) && ( response.headers.get( 'X-WP-TotalPages' ) !== null ) ) {
      setArticlePageCount( response.headers.get( 'X-WP-TotalPages' ) );
    }

    const responseData = await response.json();

    return responseData;
  };

  const makeRequest = ( endpoint, method, body ) => {
    setupRequest( endpoint, method, body )
      .then( ( response ) => {
        setArticleData( articleData => [ ...articleData, ...response ] );
      })
      .catch( ( error ) => {
        console.error( error );
      })
      .finally( () => {
        setLoading( false );
      });
  };

  const loadMoreArticles = () => {
    setLoading( true );
    let url = `wp-json/wp/v2/posts/?per_page=12&page=${articlePageCurrent}`;
    if ( archiveType === 'category' ) {
      url += `&categories=${window.DP.archive_data.cat_ID}`;
    }
    if ( archiveType === 'tag' ) {
      url += `&tags=${window.DP.archive_data.term_id}`;
    }
    makeRequest( url, 'GET' );
    setArticlePageCurrent( articlePageCurrent + 1 );
  };

  useEffect( () => {
    if ( queries.postsArchive ) {
      setArchiveType( 'posts' );
      setArchiveTitle( window.DP.blog_archive_page_header_text );
    } else if ( queries.categoryArchive ) {
      setArchiveType( 'category' );
      setArchiveTitle( window.DP.archive_data.name );
    } else if ( queries.tagArchive ) {
      setArchiveType( 'tag' );
      setArchiveTitle( window.DP.archive_data.name );
    }
  }, [ queries ] );

  useEffect( () => {
    if ( archiveType !== '' ) {
      loadMoreArticles();
    }
  }, [ archiveType ] );

  return (
    <div className={`${ archiveType !== '' && archiveType !== 'posts' ? 'taxonomy-archive' : 'posts-archive' }`}>
      <div className='posts-archive-header relative px-[20px] md:px-[50px] min-h-[140px]'>
        <div className="posts-archive-header-text lg:m-auto max-w-lg py-[45px] text-center">
          <h2 className="heading-2">{ archiveTitle }</h2>
        </div>
      </div>
      <div className="px-[20px] md:px-[50px]">
        <div className="posts-container lg:m-auto max-w-lg">
          { ( archiveType !== '' && archiveType !== 'posts' ) &&
            <p className="breadcrumbs"><a href="/posts/">{ window.DP.blog_archive_page_header_text }</a> &gt; { archiveTitle }</p>
          }
          <ul className="search__list-item list-none pl-0 grid gap-35 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            { articleData.map( ( article ) => {
              return (
                <div className="post w-full" key={ self.crypto.randomUUID() }>
                  <a href={ article.link }>
                    <div className="min-h-[80px] sm:min-h-[100px] md:min-h-[160px] aspect-[4/3] bg-[#c4c4c4] relative overflow-hidden">
                      <img className="object-cover object-center absolute inset-0 w-full h-full" src={article.image?.url} alt={ article.image?.alt } />
                    </div>
                  </a>
                  <h3 className="header-3 mt-16 mb-5 ml-0"><a href={ article.link }><strong dangerouslySetInnerHTML={ { __html:article.title.rendered } } /></a></h3>
                  <div className="archive-excerpt" dangerouslySetInnerHTML={ { __html: article.excerpt.rendered } }></div>
                  <p className="read-more ml-0">
                    <a className="no-underline hover:underline text-sm" href={ article.link }><strong>Read More &gt;</strong></a>
                  </p>
                </div>
              );
            })}

          </ul>
          { ( articlePageCurrent <= articlePageCount ) &&
            <LoadMoreButton makeRequest={ loadMoreArticles } loading={ loading } />
          }
        </div>
      </div>
    </div>
  );
};


const renderPostsArchive = () => {
  const postsArchive = document.querySelector( '.posts-archive #main' );
  const tagArchive = document.querySelector( '.tag #main' );
  const categoryArchive = document.querySelector( '.category #main' );

  let root;

  if ( postsArchive ) {
    root = createRoot( postsArchive );
  } else if ( tagArchive ) {
    root = createRoot( tagArchive );
  } else if ( categoryArchive ) {
    root = createRoot( categoryArchive );
  } else {
    return;
  }

  const queries = { postsArchive, tagArchive, categoryArchive };

  if ( !root ) return;

  root.render( <PostsArchive queries={ queries } /> );
};

export default renderPostsArchive;
